import { SearchIcon } from '@/components/icons/search-icon';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { ValidationError, ValidationErrorComponent } from '@/components/validation-error/validation-error';
import { regexes } from '@/constants/regexes';
import { useZipCodeTheaterSearch, UseZipCodeTheaterSearchByType } from '@/hooks/theater-zip-code-search';
import { createFormErrorHandler } from '@/utils/form-error-handler';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

type ZipLocationSearchComponentProps = {
  onResults?: (results: PagedListOfTheater, zipCode?: string) => void;
  type: UseZipCodeTheaterSearchByType;
};

export const ZipLocationSearchComponent = ({ onResults, type }: ZipLocationSearchComponentProps): React.JSX.Element => {
  const [searching, setSearching] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const { searchByZip } = useZipCodeTheaterSearch();

  const formSchema = z.object({
    zipCode: z.string({
      message: 'ZIP code cannot be empty.',
    }).regex(regexes.zipCode, {
      message: 'The value for the ZIP Code field is invalid.',
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      zipCode: '',
    },
    resolver: zodResolver(formSchema),
  });

  const handleFormErrors = createFormErrorHandler<z.infer<typeof formSchema>>(setValidationErrors);

  const onSubmit = async (values: z.infer<typeof formSchema>): Promise<void> => {
    setSearching(true);
    setValidationErrors([]);
    const result = await searchByZip(values.zipCode, type);
    if (result.error) {
      setValidationErrors([{ messages: [result.error] }]);
    } else {
      onResults?.(result.data, values.zipCode);
    }
    setSearching(false);
  };

  if (type === 'compact-search-and-redirect') {
    return (
      <div className="flex flex-col gap-sm w-full">
        <ValidationErrorComponent errors={validationErrors} />

        <form onSubmit={form.handleSubmit(onSubmit, handleFormErrors)}>
          <Controller control={form.control}
            name="zipCode"
            render={({ field, fieldState }) =>
              <InputGroup className="h-8">
                <InputGroupInput {...field}
                  aria-invalid={fieldState.invalid}
                  className="h-8"
                  data-test="input-zip-code"
                  id="input-zip-code"
                  maxLength={5}
                  type="number"
                />
                <InputGroupAddon align="inline-end"
                  className="h-full p-0 m-0"
                >
                  <InputGroupButton aria-label="Search"
                    className="size-8"
                    data-test="button-zip-code-search"
                    disabled={searching}
                    type="submit"
                    variant="default"
                  >
                    <SearchIcon className="fill-white size-md" />
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
            }
          />
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm w-full">
      <ValidationErrorComponent errors={validationErrors} />

      <form onSubmit={form.handleSubmit(onSubmit, handleFormErrors)}>
        <Controller control={form.control}
          name="zipCode"
          render={({ field, fieldState }) =>
            <Field className="w-[75%] self-center md:w-full">
              <FieldLabel required
                className="sr-only"
                error={!!fieldState.error}
                htmlFor="input-zip-code"
              >
                ZIP Code
              </FieldLabel>
              <Input {...field}
                aria-invalid={fieldState.invalid}
                data-test="input-zip-code"
                id="input-zip-code"
                maxLength={5}
                type="number"
              />
            </Field>
          }
        />

        <div className="mt-sm">
          <Button className="w-full"
            data-test="button-zip-code-search"
            disabled={searching}
            loadingCenter="within"
            showLoading={searching}
            type="submit"
          >
            {searching ? 'Searching' : 'Search by ZIP Code'}
          </Button>
        </div>
      </form>
    </div>
  );
};
